/**
 * Socket Integration Module
 *
 * This module sets up socket listeners that update Zustand stores directly.
 * It handles real-time updates for tasks, including creation, updates, deletion, and movement.
 */

import socket from '../socket/socketClient';
import useTaskStore from './taskStore';
import useAuthStore from './authStore';

/**
 * Get the current user's ID from the auth store
 * @returns {string|null} - Current user ID or null
 */
const getCurrentUserId = () => {
  const user = useAuthStore.getState().user;
  return user?._id || user?.id || null;
};

/**
 * Initialize socket event listeners for real-time task updates
 * This function should be called once when the application starts
 */
export const initializeSocketIntegration = () => {
  const taskStore = useTaskStore.getState();

  console.log('🔌 Initializing socket integration for tasks...');

  // Task created - Add new task to all relevant collections
  socket.on('task:created', (event) => {
    console.log('📝 Task created event received:', event);

    const task = event.payload?.task || event.task;

    if (task) {
      // Add task to collections (handles all state updates)
      taskStore.addTaskToList(task);
    }
  });

  // Task updated - Update task in all collections
  socket.on('task:updated', (event) => {
    console.log('✏️ Task updated event received:', event);

    const task = event.payload?.task || event.task;

    if (task) {
      // Update task in all collections
      taskStore.updateTaskInCollections(task);
    }
  });

  // Task deleted - Remove task from all collections
  socket.on('task:deleted', (event) => {
    console.log('🗑️ Task deleted event received:', event);

    const taskId = event.payload?.taskId || event.taskId;
    const projectId = event.payload?.projectId || event.projectId;

    if (taskId) {
      // Remove task from all collections
      if (projectId) {
        taskStore.removeTaskFromCollections(taskId, projectId);
      } else {
        taskStore.removeTaskFromList(taskId);
      }
    }
  });

  // Task moved - Update task position/status in collections
  socket.on('task:moved', (event) => {
    console.log('🔄 Task moved event received:', event);

    const task = event.payload?.task || event.task;

    if (task) {
      // Update task in all collections (position/status change)
      taskStore.updateTaskInCollections(task);
    }
  });

  // Task status updated
  socket.on('task:statusUpdated', (event) => {
    console.log('📊 Task status updated event received:', event);

    const task = event.payload?.task || event.task;

    if (task) {
      taskStore.updateTaskInCollections(task);
    }
  });

  // Task assignment changed
  socket.on('task:assignmentChanged', (event) => {
    console.log('👤 Task assignment changed event received:', event);

    const task = event.payload?.task || event.task;

    if (task) {
      taskStore.updateTaskInCollections(task);
    }
  });

  // Task priority changed
  socket.on('task:priorityChanged', (event) => {
    console.log('⚡ Task priority changed event received:', event);

    const task = event.payload?.task || event.task;

    if (task) {
      taskStore.updateTaskInCollections(task);
    }
  });

  // Task comment added
  socket.on('task:commentAdded', (event) => {
    console.log('💬 Task comment added event received:', event);

    const task = event.payload?.task || event.task;

    if (task) {
      taskStore.updateTaskInCollections(task);
    }
  });

  console.log('✅ Socket integration initialized successfully');
};

/**
 * Clean up socket event listeners
 * This function should be called when the application unmounts or user logs out
 */
export const cleanupSocketIntegration = () => {
  console.log('🔌 Cleaning up socket integration...');

  socket.off('task:created');
  socket.off('task:updated');
  socket.off('task:deleted');
  socket.off('task:moved');
  socket.off('task:statusUpdated');
  socket.off('task:assignmentChanged');
  socket.off('task:priorityChanged');
  socket.off('task:commentAdded');

  console.log('✅ Socket integration cleaned up successfully');
};

export default {
  initializeSocketIntegration,
  cleanupSocketIntegration,
};
